/**
 * Keep tabs in step about who is signed in.
 *
 * Every tab on this site shares one localStorage, and that is where the token
 * and the signed-in user live. So signing in as somebody else in a second tab
 * overwrites the first tab's session underneath it — the first tab carries on
 * polling every five seconds, now sending the *other* account's token while its
 * screen is still built from the previous account's data.
 *
 * Reported on 21 August 2026: signed in as one account, opened another tab,
 * signed in as a second account without logging out of the first, and the first
 * tab began failing and then crashed. Nothing was watching for it, so the tab
 * had no way of knowing its own session had been replaced.
 *
 * The browser does tell us: a `storage` event fires in every *other* tab when
 * localStorage changes. Three cases, and they want different answers:
 *
 *   signed out elsewhere        -> go to sign in; there is no session left
 *   signed in as someone else   -> reload, so the screen is rebuilt for them
 *   same person, new token      -> nothing to do, the next request uses it
 *
 * Reloading rather than trying to re-render in place is deliberate: half the
 * tree holds the previous account's data — their conversations, their property,
 * their balance — and patching that live is how one customer ends up seeing
 * another's records.
 */

const idOf = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw)?._id || null;
  } catch {
    return null;
  }
};

export default function watchForSessionChange() {
  let knownId = idOf(window.localStorage.getItem("user"));

  window.addEventListener("storage", (event) => {
    // Only the two keys that decide who we are. Ignore everything else.
    if (event.key !== "user" && event.key !== "token") return;

    const token = window.localStorage.getItem("token");
    const currentId = idOf(window.localStorage.getItem("user"));

    if (!token || !currentId) {
      // Signed out in another tab. Nothing here can succeed any more.
      if (window.location.pathname !== "/sign-in") {
        window.location.replace("/sign-in");
      }
      return;
    }

    if (knownId && currentId !== knownId) {
      // A different person is signed in now. This screen belongs to the last
      // one, so rebuild it rather than mixing the two.
      knownId = currentId;
      window.location.reload();
      return;
    }

    // Same person, refreshed token. The next request picks it up by itself.
    knownId = currentId;
  });
}
